import { AxiosError } from 'axios'
import notificationService from '@app/services/notificationService'

import {
  parseS3ErrorFromXml,
  createS3ErrorDetails,
  isS3ErrorStatus,
  isS3ErrorRetryable,
  isS3AuthError,
  isS3FileSizeError
} from './s3ErrorParser'

import type { S3ErrorDetails } from '@app/types/s3Error'

export class S3Error extends Error {
  public readonly details: S3ErrorDetails
  public readonly isRetryable: boolean
  public readonly isAuthError: boolean
  public readonly isFileSizeError: boolean

  constructor (details: S3ErrorDetails) {
    super(details.userMessage)
    this.name = 'S3Error'
    this.details = details
    this.isRetryable = isS3ErrorRetryable({ code: details.code, message: details.message })
    this.isAuthError = isS3AuthError({ code: details.code, message: details.message })
    this.isFileSizeError = isS3FileSizeError({ code: details.code, message: details.message })
  }
}

/**
 * Обрабатывает ошибку от S3 и создает соответствующее уведомление
 */
export const handleS3Error = (error: S3Error): void => {
  const { details } = error

  notificationService.error({
    message: 'Ошибка загрузки файла',
    description: details.userMessage
  })
}

/**
 * Обрабатывает AxiosError от S3 загрузки
 */
export const handleS3AxiosError = (error: AxiosError): S3Error | null => {
  const { response } = error

  if (!response || !isS3ErrorStatus(response.status)) {
    return null
  }

  const responseText = typeof response.data === 'string' ? response.data : String(response.data)
  const s3Error = parseS3ErrorFromXml(responseText)

  if (!s3Error) {
    const details = createS3ErrorDetails(response.status, {
      code: 'UnknownError',
      message: `HTTP ${response.status}: ${response.statusText}`
    })
    return new S3Error(details)
  }

  const details = createS3ErrorDetails(response.status, s3Error)
  return new S3Error(details)
}

/**
 * Определяет, нужно ли повторять загрузку после ошибки S3
 */
export const shouldRetryS3Upload = (error: S3Error): boolean => {
  if (error.isAuthError) {
    return false
  }

  if (error.isFileSizeError) {
    return false
  }

  return error.isRetryable
}

/**
 * Получает задержку перед повторной попыткой загрузки
 */
export const getS3RetryDelay = (attempt: number): number => {
  return Math.min(1000 * Math.pow(2, attempt), 30000) // Максимум 30 секунд
}
